import ChevronDownSvg from "@redotech/redo-icons/chevron-down_filled.svg";
import ChevronUpSvg from "@redotech/redo-icons/chevron-up_filled.svg";
import {
  ForwardedRef,
  forwardRef,
  JSXElementConstructor,
  memo,
  MouseEvent,
  ReactNode,
} from "react";
import {
  RedoButton,
  RedoButtonHierarchy,
  RedoButtonSize,
  RedoButtonTheme,
} from "./redo-button";
import { RedoButtonGroup } from "./redo-button-group";

/**
 * @param children -- this should be your dropdown component already wrapped in the dropdown
 */
export interface RedoDropdownButtonProps {
  hierarchy?: RedoButtonHierarchy;
  text: string;
  onClick(event: MouseEvent): void;
  size?: RedoButtonSize;
  IconLeading?: JSXElementConstructor<any>;
  IconTrailing?: JSXElementConstructor<any>;
  dontRestrainIconWidth?: boolean;
  disabled?: boolean;
  pending?: boolean;
  children: ReactNode;
  dropdownOpen: boolean;
  setDropdownOpen(value: boolean): void;
  theme?: RedoButtonTheme;
  mainButtonStretchHorizontal?: boolean;
  mainButtonRef?: (ref: HTMLButtonElement) => void;
  refSetToContainer?: boolean;
  dataTestId?: string;
  dataTrackId?: string;
  squared?: boolean;
}

export const RedoButtonDropdown = memo(
  forwardRef(function RedoButtonDropdown(
    {
      hierarchy = "primary",
      size,
      IconLeading,
      IconTrailing,
      dontRestrainIconWidth,
      disabled,
      pending,
      text,
      onClick,
      children,
      dropdownOpen,
      setDropdownOpen,
      theme = "normal",
      mainButtonStretchHorizontal = false,
      refSetToContainer = false,
      mainButtonRef,
      dataTestId,
      dataTrackId,
      squared = false,
    }: RedoDropdownButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    return (
      <>
        <RedoButtonGroup ref={refSetToContainer ? ref : undefined}>
          <RedoButton
            dataTrackId={dataTrackId}
            disabled={disabled}
            dontRestrainIconWidth={dontRestrainIconWidth}
            flexGrow={mainButtonStretchHorizontal ? 1 : undefined}
            hierarchy={hierarchy}
            IconLeading={IconLeading}
            IconTrailing={IconTrailing}
            onClick={onClick}
            pending={pending}
            ref={mainButtonRef}
            size={size}
            squared={squared}
            text={text}
            theme={theme}
          />
          <RedoButton
            aria-label={dropdownOpen ? "Collapse" : "Expand"}
            dataTestId={dataTestId}
            disabled={disabled || pending}
            hierarchy={hierarchy}
            IconLeading={dropdownOpen ? ChevronUpSvg : ChevronDownSvg}
            onClick={() => {
              setDropdownOpen(!dropdownOpen);
            }}
            ref={refSetToContainer ? undefined : ref}
            size={size}
            squared={squared}
            theme={theme}
          />
        </RedoButtonGroup>
        {children}
      </>
    );
  }),
);
